import { useState, useEffect } from 'react';
import client from '../../api/client';
import betClient from '../../api/betClient';
import AiInsightCard from './AiInsightCard';
import Tip from '../ui/BettingTooltip';

const inputCls = "px-3 py-1.5 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] text-[14px] text-[#111827] dark:text-[#e2e8f0] placeholder-[#9CA3AF] focus:outline-none focus:border-[#005BB3] dark:focus:border-[#3b82f6] transition-colors duration-150";

function StatCard({ label, primary, secondary }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-3 py-2.5">
      <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6] mb-1" style={{ fontFamily: 'var(--font-sans)' }}>{label}</p>
      <p className="text-[15px] font-bold text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-mono)' }}>{primary}</p>
      {secondary && <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mt-0.5" style={{ fontFamily: 'var(--font-sans)' }}>{secondary}</p>}
    </div>
  );
}

function RecordBadge({ record, type = 'ats' }) {
  if (!record) return null;
  if (type === 'ou') {
    return (
      <span className="text-[13px] font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>
        <span className="text-[#029456]">{record.overs}O</span>
        {' - '}
        <span className="text-[#DC2626]">{record.unders}U</span>
        {record.pushes > 0 && <span className="text-[#6B7280]"> - {record.pushes}P</span>}
      </span>
    );
  }
  return (
    <span className="text-[13px] font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>
      <span className="text-[#029456]">{record.wins}W</span>
      {' - '}
      <span className="text-[#DC2626]">{record.losses}L</span>
      {record.pushes > 0 && <span className="text-[#6B7280]"> - {record.pushes}P</span>}
    </span>
  );
}

function GameRow({ game, type }) {
  const isAts = type === 'ats';
  const isOu = type === 'ou';
  const resultColor = isAts
    ? game.ats_result === 'win' ? 'text-[#029456]' : game.ats_result === 'loss' ? 'text-[#DC2626]' : 'text-[#6B7280]'
    : isOu
    ? game.ou_result === 'over' ? 'text-[#029456]' : game.ou_result === 'under' ? 'text-[#DC2626]' : 'text-[#6B7280]'
    : game.won ? 'text-[#029456]' : 'text-[#DC2626]';

  const resultLabel = isAts ? game.ats_result?.toUpperCase()
    : isOu ? game.ou_result?.toUpperCase()
    : game.won ? 'WIN' : 'LOSS';

  const date = new Date(game.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <tr className="border-b border-[#E5E7EB] dark:border-[#1e2536] text-[14px]">
      <td className="py-2 px-3 text-[#6B7280]" style={{ fontFamily: 'var(--font-mono)' }}>{date}</td>
      <td className="py-2 pr-2 text-[#1F2937] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>
        <span className="text-[12px] text-[#6B7280] mr-1">{game.location === 'home' ? 'vs' : '@'}</span>
        {game.opponent}
      </td>
      <td className="py-2 pr-2 text-[#1F2937] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-mono)' }}>{game.score}</td>
      <td className="py-2 pr-2 text-[#6B7280]" style={{ fontFamily: 'var(--font-mono)' }}>
        {isAts && game.spread != null && (game.spread > 0 ? `+${game.spread}` : game.spread)}
        {isOu && game.closing_total != null && `O/U ${game.closing_total}`}
        {!isAts && !isOu && game.closing_odds != null && (game.closing_odds > 0 ? `+${game.closing_odds}` : game.closing_odds)}
      </td>
      <td className={`py-2 font-semibold ${resultColor}`} style={{ fontFamily: 'var(--font-mono)' }}>{resultLabel}</td>
    </tr>
  );
}

const ANALYSIS_TABS = [
  { key: 'ats', label: 'ATS', tip: 'ats' },
  { key: 'ou', label: 'O/U', tip: 'overunder' },
  { key: 'ml', label: 'Moneyline', tip: 'moneyline' },
];

export default function TeamAnalysis({ sport }) {
  const [team, setTeam] = useState('');
  const [teams, setTeams] = useState([]);
  const [season, setSeason] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [analysisTab, setAnalysisTab] = useState('ats');

  const [atsData, setAtsData] = useState(null);
  const [ouData, setOuData] = useState(null);
  const [mlData, setMlData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch team list and seasons when sport changes
  useEffect(() => {
    setTeam(''); setSeason('');
    setAtsData(null); setOuData(null); setMlData(null);

    client.get(`/api/odds/${sport}`)
      .then((r) => {
        const names = new Set();
        for (const ev of r.data || []) {
          if (ev.home_team) names.add(ev.home_team);
          if (ev.away_team) names.add(ev.away_team);
        }
        if (names.size > 0) {
          setTeams([...names].sort());
        } else {
          // Fallback: pull from historical data for off-season sports
          client.get(`/api/patterns/teams?sport=${sport}`)
            .then((r2) => setTeams(r2.data?.teams || []))
            .catch(() => setTeams([]));
        }
      })
      .catch(() => {
        client.get(`/api/patterns/teams?sport=${sport}`)
          .then((r2) => setTeams(r2.data?.teams || []))
          .catch(() => setTeams([]));
      });

    client.get(`/api/patterns/seasons?sport=${sport}`)
      .then((r) => {
        const s = r.data?.seasons || [];
        setSeasons(s);
        if (s.length > 0) setSeason(s[0]);
      })
      .catch(() => setSeasons([]));
  }, [sport]);

  function fetchAll() {
    if (!team) return;
    setLoading(true); setError(null);
    setAtsData(null); setOuData(null); setMlData(null);

    const params = new URLSearchParams({ team, sport });
    if (season) params.set('season', season);

    Promise.all([
      betClient.get(`/api/patterns/team/ats?${params.toString()}&lastN=10`),
      betClient.get(`/api/patterns/team/over-under?${params.toString()}&lastN=10`),
      betClient.get(`/api/patterns/team/moneyline?${params.toString()}&lastN=10`),
    ])
      .then(([ats, ou, ml]) => {
        setAtsData(ats.data);
        setOuData(ou.data);
        setMlData(ml.data);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  const activeData = analysisTab === 'ats' ? atsData : analysisTab === 'ou' ? ouData : mlData;
  const activeType = analysisTab === 'ats' ? 'team_ats' : analysisTab === 'ou' ? 'team_ou' : 'team_ml';

  return (
    <div className="max-w-[1100px]">
      {/* Team selector */}
      <div className="flex items-end gap-2 mb-4">
        <div>
          <label className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] block mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Team</label>
          <select value={team} onChange={(e) => setTeam(e.target.value)} className={`w-[300px] ${inputCls}`}>
            <option value="">Select a team...</option>
            {teams.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] block mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Season</label>
          <select value={season} onChange={(e) => setSeason(e.target.value)} className={`w-[150px] ${inputCls}`}>
            {seasons.length === 0 && <option value="">No data</option>}
            {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={fetchAll} disabled={loading || !team}
          className="px-4 py-1.5 rounded-md bg-[#005BB3] hover:bg-[#004494] disabled:opacity-40 text-white text-[14px] font-medium transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
          {loading ? 'Loading...' : 'Analyze'}
        </button>
      </div>

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-3">
          <p className="text-[#DC2626] text-[12px]">Failed to load: {error}</p>
          <button onClick={fetchAll} className="text-[11px] text-[#DC2626] underline mt-1">Retry</button>
        </div>
      )}

      {!loading && !activeData && !error && (
        <div className="py-12 text-center">
          <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Select a team and click Analyze to see ATS record, over/under trends, and moneyline performance across the season.</p>
        </div>
      )}

      {loading && (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}
        </div>
      )}

      {activeData && !loading && (
        <>
          {/* Sub-tabs */}
          <div className="flex items-end gap-0 mb-4 border-b border-[#E5E7EB] dark:border-[#1e2536] pb-0">
            {ANALYSIS_TABS.map((t) => (
              <button key={t.key} onClick={() => setAnalysisTab(t.key)}
                className={`px-0 mr-6 pb-2 text-[14px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
                  analysisTab === t.key
                    ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
                    : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#374151] dark:hover:text-[#e2e8f0]'
                }`}
                style={{ fontFamily: 'var(--font-sans)' }}>
                {t.label}
              </button>
            ))}
          </div>

          {analysisTab === 'ats' && atsData && <AtsSection data={atsData} />}
          {analysisTab === 'ou' && ouData && <OuSection data={ouData} />}
          {analysisTab === 'ml' && mlData && <MlSection data={mlData} />}

          <div className="flex flex-col lg:flex-row gap-4 items-start mt-4">
            <div className="w-full lg:w-1/2 min-w-0">
              <GamesTable games={activeData.games || []} type={analysisTab} totalGames={activeData.total_games || 0} />
            </div>
            <div className="w-full lg:w-1/2 min-w-0">
              <AiInsightCard
                key={`${activeType}-${team}-${sport}`}
                analysisType={activeType}
                params={{ team, sport, season: season || undefined, lastN: 10 }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function AtsSection({ data }) {
  const r = data.record;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-0">
      <StatCard label={<Tip term="ats">Overall ATS</Tip>} primary={<RecordBadge record={r.overall} />} secondary={`${(r.overall.pct * 100).toFixed(1)}% cover rate`} />
      <StatCard label="Home ATS" primary={<RecordBadge record={r.home} />} secondary={`${(r.home.pct * 100).toFixed(1)}%`} />
      <StatCard label="Away ATS" primary={<RecordBadge record={r.away} />} secondary={`${(r.away.pct * 100).toFixed(1)}%`} />
      {r.last_n && <StatCard label={`Last ${r.last_n.n} ATS`} primary={<RecordBadge record={r.last_n} />} secondary={`${(r.last_n.pct * 100).toFixed(1)}%`} />}
    </div>
  );
}

function OuSection({ data }) {
  const r = data.record;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-0">
      <StatCard label="Overall O/U" primary={<RecordBadge record={r.overall} type="ou" />} secondary={`${(r.overall.over_pct * 100).toFixed(1)}% over rate`} />
      <StatCard label="Home O/U" primary={<RecordBadge record={r.home} type="ou" />} secondary={`${(r.home.over_pct * 100).toFixed(1)}%`} />
      <StatCard label="Away O/U" primary={<RecordBadge record={r.away} type="ou" />} secondary={`${(r.away.over_pct * 100).toFixed(1)}%`} />
      <StatCard label="Avg Total" primary={data.avg_total_score} secondary="points per game" />
    </div>
  );
}

function MlSection({ data }) {
  const s = data.summary;
  const edge = s.edge;
  const edgeColor = edge > 0 ? 'text-[#029456]' : edge < 0 ? 'text-[#DC2626]' : 'text-[#6B7280]';
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-0">
      <StatCard label="ML Record"
        primary={<span style={{ fontFamily: 'var(--font-mono)' }}><span className="text-[#029456]">{s.wins}W</span> - <span className="text-[#DC2626]">{s.losses}L</span></span>}
        secondary={`${(s.actual_win_pct * 100).toFixed(1)}% win rate`}
      />
      <StatCard label={<Tip term="impliedprob">Avg Implied Prob</Tip>} primary={`${(s.avg_implied_prob * 100).toFixed(1)}%`} secondary="market expectation" />
      <StatCard label={<Tip term="value">Edge vs Odds</Tip>} primary={<span className={edgeColor}>{edge > 0 ? '+' : ''}{(edge * 100).toFixed(1)}%</span>} secondary={edge > 0 ? 'outperforming' : edge < 0 ? 'underperforming' : 'neutral'} />
      {s.last_n && <StatCard label={`Last ${s.last_n.n}`}
        primary={<span style={{ fontFamily: 'var(--font-mono)' }}><span className="text-[#029456]">{s.last_n.wins}W</span> - <span className="text-[#DC2626]">{s.last_n.losses}L</span></span>}
        secondary={`${(s.last_n.actual_win_pct * 100).toFixed(1)}% win rate`}
      />}
    </div>
  );
}

function GamesTable({ games, type, totalGames }) {
  const [expanded, setExpanded] = useState(false);
  const displayed = expanded ? games : games.slice(-10);

  return (
    <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] overflow-x-auto">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E7EB] dark:border-[#1e2536]">
        <div className="flex items-center gap-2">
          <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
          <h3 className="text-[12px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Game Log</h3>
        </div>
        <span className="text-[12px] text-[#6B7280]" style={{ fontFamily: 'var(--font-mono)' }}>{totalGames} games</span>
      </div>
      <table className="w-full text-left">
        <thead>
          <tr className="text-[12px] uppercase tracking-[0.5px] text-[#6B7280] border-b border-[#E5E7EB] dark:border-[#1e2536]">
            <th className="py-2 px-3 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Date</th>
            <th className="py-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Opponent</th>
            <th className="py-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Score</th>
            <th className="py-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>{type === 'ats' ? 'Spread' : type === 'ou' ? 'Total' : 'Odds'}</th>
            <th className="py-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Result</th>
          </tr>
        </thead>
        <tbody>
          {displayed.map((g, i) => <GameRow key={g.event_id || i} game={g} type={type} />)}
        </tbody>
      </table>
      {games.length > 10 && (
        <button onClick={() => setExpanded(!expanded)}
          className="w-full py-2 text-[13px] text-[#005BB3] dark:text-[#3b82f6] hover:bg-[#F8F9FA] dark:hover:bg-[#0f1219] transition-colors duration-150 border-t border-[#E5E7EB] dark:border-[#1e2536]" style={{ fontFamily: 'var(--font-sans)' }}>
          {expanded ? 'Show recent 10' : `Show all ${games.length} games`}
        </button>
      )}
    </div>
  );
}
